import { isNil } from 'lodash-es'
import { createSelector, Selector } from '@reduxjs/toolkit'
import { EntityType } from 'api'
import { DataHelper, EntityHelpers, EntityUpdateTypeFor, EntityTypeFor } from 'model'
import { GlobalState } from 'store'
import { compareInsensitive, ParametricSelector } from 'utils'

export function createEntityStoreSelector<TEntityType extends EntityType>(entityType: TEntityType) {
  return (state: GlobalState) => state.repositoryStore[entityType]
}

export function createAllEntityUpdatesSelector<TEntityType extends EntityType>(entityType: TEntityType) {
  return createSelector([createEntityStoreSelector(entityType)], entityStore => Object.values(entityStore).map(entry => entry.lastUpdate))
}

export const createAllEntitiesSelector = <TEntityType extends EntityType>(entityType: TEntityType) =>
  createSelector(
    [createAllEntityUpdatesSelector(entityType)],
    entityUpdates => entityUpdates.map(update => update.entity) as EntityTypeFor<TEntityType>[]
  )

export function createAllEntitiesSortedSelector<TEntityType extends EntityType>(entityType: TEntityType) {
  const dataHelper = EntityHelpers[entityType] as DataHelper<any>

  return createSelector(
    [createAllEntityUpdatesSelector(entityType)],
    entityUpdates =>
      entityUpdates
        .sort((update1, update2) => compareInsensitive(dataHelper.getName(update1.entity), dataHelper.getName(update2.entity)))
        .map(update => update.entity) as EntityTypeFor<TEntityType>[]
  )
}

export function createSortedEntityIdsSelector<TEntityType extends EntityType>(entityType: TEntityType): Selector<GlobalState, string[]> {
  const dataHelper = EntityHelpers[entityType] as DataHelper<any>

  return createSelector([createAllEntityUpdatesSelector(entityType)], entityUpdates =>
    entityUpdates
      .sort((update1, update2) => compareInsensitive(dataHelper.getName(update1.entity), dataHelper.getName(update2.entity)))
      .map(update => update.entityId)
  )
}

export function createEntitySelector<TProps, TEntityType extends EntityType>(
  entityIdSelector: ParametricSelector<{}, TProps, string | null | undefined>,
  entityType: TEntityType
) {
  return createSelector(
    [entityIdSelector, createEntityStoreSelector(entityType)],
    (entityId, entityStore) =>
      (isNil(entityId) ? null : entityStore[entityId] ? entityStore[entityId].lastUpdate.entity : null) as EntityTypeFor<TEntityType> | null
  )
}

export function createEntityUpdateSelector<TProps, TEntityType extends EntityType>(
  entityIdSelector: ParametricSelector<{}, TProps, string | null | undefined>,
  entityType: TEntityType
) {
  return createSelector([entityIdSelector, createEntityStoreSelector(entityType)], (entityId, entityStore) =>
    isNil(entityId) ? null : entityStore[entityId] ? (entityStore[entityId].lastUpdate as any) : null
  )
}

export type EntityGetter<T> = (entityId: string) => T | null

export function createEntityGetterSelector<TEntityType extends EntityType>(entityType: TEntityType) {
  return createSelector([createEntityStoreSelector(entityType)], entityStore => (entityId: string | null | undefined) =>
    (entityId && entityStore[entityId] ? entityStore[entityId].lastUpdate.entity : null) as EntityTypeFor<TEntityType> | null
  )
}

export function createEntityUpdateGetterSelector<TEntityType extends EntityType>(
  entityType: TEntityType
): Selector<GlobalState, (entityId: string) => EntityUpdateTypeFor<TEntityType> | null> {
  return createSelector([createEntityStoreSelector(entityType)], entityStore => (entityId: string) =>
    entityStore[entityId] ? (entityStore[entityId].lastUpdate as any) : null
  )
}

export function createEntityListSelector<TProps, TEntityType extends EntityType>(
  entityIdsSelector: ParametricSelector<{}, TProps, string[]>,
  entityType: TEntityType
) {
  return createSelector([entityIdsSelector, createEntityStoreSelector(entityType)], (entityIds, entityStore) =>
    entityIds.map(entityId => (entityStore[entityId] ? entityStore[entityId].lastUpdate.entity : null) as EntityTypeFor<TEntityType> | null)
  )
}

export function createEntityHelperSelector<TEntityType extends EntityType>(
  entityType: TEntityType
): Selector<{}, DataHelper<EntityTypeFor<TEntityType>>> {
  return () => EntityHelpers[entityType] as DataHelper<EntityTypeFor<TEntityType>>
}
